import AgGridReactTable from "@components/ag-grid/AgGridReactTable";
import AgGridSkeleton from "@components/ag-grid/AgGridSkeleton";
import CopyButton from "@components/buttons/CopyButton";
import LightCustomButton from "@components/buttons/LightCustomButton";
import TableActionButton from "@components/buttons/TableActionButton";
import GlobalSearchInputField from "@components/inputs/GlobalSearchInputField";
import { walletBlocklistService } from "@services/apis/WalletBlocklist";
import { WalletBlocklistEntry } from "@services/models/WalletBlocklist/WalletBlocklistModel";
import useIsMobile from "@utils/hooks/useIsMobile";
import { formatDisplayDate } from "@utils/safeDate";
import { ColDef } from "ag-grid-community";
import {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import useSnackbarMessage from "../../utils/HandleSuccessErrorSnackbars";
import AddWalletBlocklistSlider from "./components/AddWalletBlocklistSlider";

const PAGE_LIMIT = 50;

type StatusFilter = "all" | "1" | "0";

const STATUS_OPTIONS: { value: StatusFilter; label: string }[] = [
  { value: "all", label: "All" },
  { value: "1", label: "Active" },
  { value: "0", label: "Inactive" },
];

const isEntryActive = (entry: WalletBlocklistEntry) =>
  Number(entry.isActive) === 1;

const WalletBlocklist = () => {
  const gridRef = useRef<any>(null);
  const { handleError, handleSuccess } = useSnackbarMessage();
  const inFlight = useRef(false);
  const requestSeq = useRef(0);
  const isMobile = useIsMobile();

  const [rows, setRows] = useState<WalletBlocklistEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearchTerm, setAppliedSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [pendingAddress, setPendingAddress] = useState<string | null>(null);
  const [addSliderOpen, setAddSliderOpen] = useState(false);

  const fetchWallets = useCallback(
    async (pageNum: number, reset: boolean = false) => {
      if (inFlight.current && !reset) return;
      const requestId = ++requestSeq.current;
      inFlight.current = true;

      try {
        setLoading(true);
        setError("");

        const offset = (pageNum - 1) * PAGE_LIMIT;
        const res = await walletBlocklistService.listWallets({
          limit: PAGE_LIMIT,
          offset,
          ...(statusFilter === "all" ? {} : { isActive: Number(statusFilter) }),
        });

        if (requestId !== requestSeq.current) return;

        if (res.success) {
          const fetched = res.data || [];
          const total = res.pagination?.total;
          setHasMore(
            typeof total === "number"
              ? offset + fetched.length < total
              : fetched.length === PAGE_LIMIT,
          );
          if (reset) {
            setRows(fetched);
          } else {
            setRows((prev) => [...prev, ...fetched]);
          }
        } else {
          if (reset) setRows([]);
          setHasMore(false);
          setError(res.msg || "Failed to fetch wallets");
        }
      } catch {
        if (requestId !== requestSeq.current) return;
        if (reset) setRows([]);
        setHasMore(false);
        setError("Failed to fetch wallets");
      } finally {
        if (requestId === requestSeq.current) {
          setLoading(false);
          inFlight.current = false;
        }
      }
    },
    [statusFilter],
  );

  const refreshList = useCallback(() => {
    setPage(1);
    setHasMore(true);
    fetchWallets(1, true);
  }, [fetchWallets]);

  useEffect(() => {
    refreshList();
  }, [refreshList]);

  useEffect(() => {
    if (page > 1 && hasMore) {
      fetchWallets(page, false);
    }
  }, [page, hasMore, fetchWallets]);

  const handleToggle = useCallback(
    async (entry: WalletBlocklistEntry) => {
      const active = isEntryActive(entry);
      setPendingAddress(entry.walletAddress);
      try {
        const response = active
          ? await walletBlocklistService.deactivateWallet({
              address: entry.walletAddress,
            })
          : await walletBlocklistService.activateWallet({
              address: entry.walletAddress,
            });
        handleSuccess({
          message:
            response.msg ||
            (active
              ? "Wallet address deactivated successfully"
              : "Wallet address activated successfully"),
        });
        refreshList();
      } catch (err: any) {
        handleError({
          message:
            err?.response?.data?.msg ||
            (active
              ? "Failed to deactivate wallet"
              : "Failed to activate wallet"),
        });
      } finally {
        setPendingAddress(null);
      }
    },
    [handleError, handleSuccess, refreshList],
  );

  const columnDefs: ColDef[] = useMemo(
    () => [
      {
        headerName: "ID",
        field: "id",
        minWidth: 70,
        maxWidth: 90,
      },
      {
        headerName: "Wallet Address",
        field: "walletAddress",
        flex: 1.6,
        minWidth: 320,
        cellRenderer: ({ data }: { data: WalletBlocklistEntry }) => (
          <div className="flex items-center justify-between space-x-[10px]">
            <span className="truncate">{data.walletAddress}</span>
            <CopyButton
              copyText={data.walletAddress}
              copyMessage="Wallet address copied!"
              copyIconSize={10}
            />
          </div>
        ),
      },
      {
        headerName: "Reason",
        field: "reason",
        flex: 1,
        minWidth: 160,
        valueFormatter: (params) => params.value || "—",
      },
      {
        headerName: "Status",
        field: "isActive",
        minWidth: 100,
        maxWidth: 120,
        cellRenderer: ({ data }: { data: WalletBlocklistEntry }) => {
          const active = isEntryActive(data);
          return (
            <span
              className={`inline-flex h-[20px] w-fit items-center self-center leading-none px-2 rounded text-[10px] ${
                active
                  ? "bg-bg-status-green text-text-status-green"
                  : "bg-bg-status-red text-text-status-red"
              }`}
            >
              {active ? "Active" : "Inactive"}
            </span>
          );
        },
      },
      {
        headerName: "Created By",
        field: "createdBy",
        minWidth: 110,
        maxWidth: 130,
      },
      {
        headerName: "Updated By",
        field: "updatedBy",
        minWidth: 110,
        maxWidth: 130,
      },
      {
        headerName: "Created At",
        field: "createdAt",
        minWidth: 170,
        valueFormatter: (params) => formatDisplayDate(params.value),
      },
      {
        headerName: "Updated At",
        field: "updatedAt",
        minWidth: 170,
        valueFormatter: (params) => formatDisplayDate(params.value),
      },
      {
        headerName: "Action",
        field: "action",
        minWidth: 140,
        maxWidth: 170,
        sortable: false,
        filter: false,
        cellRenderer: ({ data }: { data: WalletBlocklistEntry }) => {
          const active = isEntryActive(data);
          const isPending = pendingAddress === data.walletAddress;
          return (
            <TableActionButton
              variant={active ? "danger" : "success"}
              icon={active ? "block" : "check_circle"}
              loading={isPending}
              onClick={(event) => {
                event.stopPropagation();
                handleToggle(data);
              }}
            >
              {active ? "Deactivate" : "Activate"}
            </TableActionButton>
          );
        },
      },
    ],
    [handleToggle, pendingAddress],
  );

  const filteredRows = useMemo(() => {
    if (!appliedSearchTerm) return rows;
    const term = appliedSearchTerm.toLowerCase();
    return rows.filter(
      (row) =>
        row.walletAddress.toLowerCase().includes(term) ||
        (row.reason?.toLowerCase().includes(term) ?? false) ||
        (row.createdBy?.toLowerCase().includes(term) ?? false) ||
        (row.updatedBy?.toLowerCase().includes(term) ?? false),
    );
  }, [rows, appliedSearchTerm]);

  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(event.target.value);
  };

  const handleSearch = () => {
    setAppliedSearchTerm(searchTerm.trim());
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setAppliedSearchTerm("");
  };

  const onBodyScroll = useCallback(
    (params: any) => {
      if (!hasMore || loading || appliedSearchTerm) return;

      const api = params.api;
      const displayedRowCount = api.getDisplayedRowCount();
      const lastDisplayedRowIndex = api.getLastDisplayedRowIndex();

      if (
        lastDisplayedRowIndex >= displayedRowCount - 5 &&
        displayedRowCount > 0
      ) {
        setPage((prev) => prev + 1);
      }
    },
    [appliedSearchTerm, hasMore, loading],
  );

  return (
    <>
      <div className="flex h-full flex-col">
        <div className="sticky top-0 z-10 flex h-[70px] flex-row border-b border-border-subtle bg-bg-page-body pl-[20px]">
          <div className="mr-4 flex w-full flex-row items-center justify-between">
            <p className="text-[14px] font-semibold text-text-base">
              Wallet Blocklist
            </p>
            <LightCustomButton
              text="ADD WALLET"
              onClick={() => setAddSliderOpen(true)}
              isActive={true}
              className="min-w-[110px] shrink-0"
            />
          </div>
        </div>

        <div
          className={`flex min-h-0 flex-1 flex-col overflow-hidden bg-bg-page-body ${
            isMobile ? "mx-3 mt-3" : "mx-4 mt-4"
          }`}
        >
          <div
            className={`mb-4 flex shrink-0 items-center justify-between gap-3 ${
              isMobile ? "flex-col items-stretch" : "flex-row"
            }`}
          >
            <div className="flex items-center justify-start space-x-3">
              <p className="shrink-0 text-[10px] font-bold text-text-base">
                Filter by Status:
              </p>
              <div className="flex space-x-3">
                {STATUS_OPTIONS.map((option) => (
                  <div
                    key={option.value}
                    onClick={
                      loading ? undefined : () => setStatusFilter(option.value)
                    }
                    className={`rounded-[100px] border px-[16px] py-[4px] text-[10px] font-medium text-text-base ${
                      loading
                        ? "cursor-not-allowed opacity-50"
                        : "cursor-pointer"
                    } ${
                      statusFilter === option.value
                        ? "border-border-button-secondary bg-bg-button-secondary"
                        : "border-border-subtle bg-bg-page-body"
                    }`}
                  >
                    {option.label}
                  </div>
                ))}
              </div>
            </div>
            <div className={isMobile ? "w-full" : "w-[360px] flex-shrink-0"}>
              <GlobalSearchInputField
                value={searchTerm}
                onChange={handleSearchChange}
                onSearch={handleSearch}
                onClear={handleClearSearch}
                disabled={loading && page === 1}
                isSearchApplied={Boolean(appliedSearchTerm)}
                placeholder="Search address / reason / admin"
              />
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col">
            {loading && page === 1 ? (
              <AgGridSkeleton
                columnDefs={columnDefs}
                style={{
                  height: "calc(100vh - 150px)",
                  width: "100%",
                  minHeight: 0,
                }}
              />
            ) : (
              <div className="flex h-full min-h-0 flex-col">
                <div className="ag-theme-alpine ag-grid-12px min-h-0 flex-1">
                  <AgGridReactTable
                    ref={gridRef}
                    rowData={filteredRows}
                    columnDefs={columnDefs}
                    onBodyScroll={onBodyScroll}
                    error={error}
                    getRowId={(params) => params.data.id.toString()}
                    style={{
                      height: "calc(100vh - 150px)",
                      width: "100%",
                      minHeight: 0,
                    }}
                  />
                </div>
                {loading && rows.length > 0 && (
                  <div className="flex items-center justify-center border-t border-border-subtle py-4">
                    <div className="flex items-center space-x-2 text-text-subtle">
                      <div className="h-4 w-4 animate-spin rounded-full border-2 border-border-subtle border-t-border-button-primary"></div>
                      <span className="text-sm">Loading more wallets...</span>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      <AddWalletBlocklistSlider
        isOpen={addSliderOpen}
        onClose={() => setAddSliderOpen(false)}
        onSuccess={refreshList}
      />
    </>
  );
};

export default WalletBlocklist;
